import React, { useCallback, useContext, useEffect, useState } from "react"
import styled from "styled-components"

import { useSearchDataTrack } from "../../hooks/useSearchDataTrack"
import { outerSpacing } from "../../theme/outerSpacing"
import { getIsStringEmptyOrSpaces } from "../../utils/validation"
import AppStateContext from "../AppContent/AppStateContext"
import PostmateContext from "../PostmateProvider/postmateContext"
import { ReactComponent as CleanTextIcon } from "./clear.svg"

const Button = styled.button`
  background-color: ${(props) => props.theme.color.blue};
  border: 0;
  color: ${(props) => props.theme.color.white};
  flex-shrink: 0;
  height: 100%;
  width: 100px;

  display: none;
  @media screen and (min-width: ${(props) => props.theme.breakpoint.sm}) {
    display: initial;
  }
`

export const Form = styled.form`
  align-items: center;
  border-radius: 4px;
  border: 1px ${(props) => props.theme.color.blue} solid;
  box-sizing: border-box;
  display: flex;

  height: 36px;
  @media screen and (min-width: ${(props) => props.theme.breakpoint.sm}) {
    height: 44px;
  }
  @media screen and (min-width: ${(props) => props.theme.breakpoint.md}) {
    height: 48px;
  }
  @media screen and (min-width: ${(props) => props.theme.breakpoint.lg}) {
    height: 52px;
  }
`

export const Input = styled.input`
  align-self: stretch;
  background-color: transparent;
  border: 0;
  caret-color: ${(props) => props.theme.color.blue};
  flex-grow: 1;
  min-width: 0;
  outline: 0;
  padding: 0 ${(props) => props.theme.space.m};

  &::-ms-clear {
    display: none;
  }
`

const Container = styled.div`
  ${outerSpacing}
  padding-top: ${(props) => props.theme.space.s};
  margin-bottom: ${(props) => props.theme.space.xxs};

  @media screen and (min-width: ${(props) => props.theme.breakpoint.sm}) {
    margin-top: ${(props) => props.theme.space.l};
    margin-bottom: ${(props) => props.theme.space.s};
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.lg}) {
    margin-bottom: 20px;
  }
`

export const ClearTextButton = styled.button.attrs({ type: "reset" })`
  margin-right: 20px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background-color: transparent;
  @media screen and (min-width: ${(props) => props.theme.breakpoint.sm}) {
    margin-right: 32px;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.lg}) {
    margin-right: 40px;
  }
`

const StyledCleanTextIcon = styled(CleanTextIcon)`
  width: 16px;
  height: 16px;
`

interface Props {
  initialQuery?: string
  onSubmit: (query: string) => void
}

const SearchBar = ({ initialQuery, onSubmit }: Props) => {
  const { fire } = useSearchDataTrack()
  const { emit } = useContext(PostmateContext)
  const { servicesConfig } = useContext(AppStateContext)

  const [queryState, setQueryState] = useState(initialQuery || "")

  // update input if query changed by iframe event
  useEffect(() => {
    setQueryState(initialQuery || "")
  }, [initialQuery])

  const search = useCallback(
    (searchQuery: string) => {
      onSubmit(searchQuery)

      window.document.body.scrollTop = 0 // For Safari
      window.document.documentElement.scrollTop = 0 // For Chrome, Firefox, IE and Opera

      fire({
        action: "Search",
        label: {
          search_text: searchQuery,
        },
      })
    },
    [fire, onSubmit],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQueryState(event.currentTarget.value)
    },
    [],
  )

  const handleReset = useCallback(() => {
    setQueryState("")
  }, [])

  const handleSubmit: React.FormEventHandler = useCallback(
    (evt) => {
      evt.preventDefault()
      if (getIsStringEmptyOrSpaces(queryState)) return

      emit("SEARCH_EVENT", {
        query: queryState,
      })
      search(queryState)
    },
    [search, queryState, emit],
  )

  return (
    <Container>
      <Form onSubmit={handleSubmit} onReset={handleReset}>
        <Input
          type="text"
          placeholder={servicesConfig.searchInput.placeholder}
          value={queryState}
          aria-label="搜尋"
          onChange={handleChange}
          autoFocus
          data-testid="universal-search-input-text"
        />
        {queryState && (
          <ClearTextButton
            type="reset"
            data-testid="universal-search-input-cancel-button"
          >
            <StyledCleanTextIcon />
          </ClearTextButton>
        )}
        <Button
          type="submit"
          data-testid="universal-search-input-submit-button"
        >
          搜尋
        </Button>
      </Form>
    </Container>
  )
}

export default React.memo(SearchBar)
